import { z } from 'zod'

/**
 * clipbook-api's health as Headline samples it every five minutes (CLI-1041), and the one summary of those
 * samples the admin route and the API page's uptime graph share. Synthetic availability, from our own
 * project, one instance's view per sample — never the contract's Monthly Uptime Percentage, which
 * clipbook-api measures from real requests (its scripts/sla-report.ts). Say "service health" wherever it
 * is shown, never "uptime SLA".
 */

export const API_HEALTH_OUTCOMES = ['ok', 'degraded', 'down', 'unreachable'] as const
export type ApiHealthOutcome = (typeof API_HEALTH_OUTCOMES)[number]

/** How often the sampler runs (deploy.yml `headline-api-health-tick`). A slot without a sample is `missing`. */
export const API_HEALTH_SAMPLE_MINUTES = 5

/** How long samples are kept (the sampler purges older ones), and so the longest range a summary reads. */
export const API_HEALTH_RETENTION_DAYS = 400

/** clipbook-api's `GET /internal/health` body, parsed at the boundary; the checks are kept as sent. */
export const clipbookApiHealthSchema = z.object({
  status: z.enum(['ok', 'degraded', 'down']),
  checkedAt: z.iso.datetime(),
  version: z.string(),
  revision: z.string().nullable(),
  instance: z.string(),
  checks: z.record(z.string(), z.unknown()),
})
export type ClipbookApiHealth = z.infer<typeof clipbookApiHealthSchema>

export const API_HEALTH_BUCKETS = { '5m': 5, '1h': 60, '1d': 1440 } as const
export type ApiHealthBucket = keyof typeof API_HEALTH_BUCKETS

export const apiHealthQuerySchema = z
  .object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    bucket: z.enum(['5m', '1h', '1d']).default('1h'),
  })
  .refine((query) => Date.parse(query.to) > Date.parse(query.from), {
    message: 'to must be after from',
  })
  .refine(
    (query) =>
      Date.parse(query.to) - Date.parse(query.from) <= API_HEALTH_RETENTION_DAYS * 86_400_000,
    { message: `At most ${API_HEALTH_RETENTION_DAYS} days` },
  )
  .refine(
    (query) =>
      (Date.parse(query.to) - Date.parse(query.from)) /
        (API_HEALTH_BUCKETS[query.bucket] * 60_000) <=
      2000,
    { message: 'Too many buckets: use a coarser bucket' },
  )
export type ApiHealthQuery = z.infer<typeof apiHealthQuerySchema>

const counts = {
  ok: z.number().int(),
  degraded: z.number().int(),
  down: z.number().int(),
  unreachable: z.number().int(),
  /** Sample slots in the bucket with no sample: nobody looked, never "down". */
  missing: z.number().int(),
}

export const apiHealthSummarySchema = z.object({
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  bucketMinutes: z.number().int(),
  /**
   * (ok + degraded) / (ok + degraded + down + unreachable), as a percentage; missing slots are out of
   * both sides. Null when no sample was taken in the range.
   */
  availabilityPercent: z.number().nullable(),
  totals: z.object(counts),
  buckets: z.array(
    z.object({
      start: z.iso.datetime(),
      ...counts,
      p50LatencyMs: z.number().int().nullable(),
      p95LatencyMs: z.number().int().nullable(),
    }),
  ),
})
export type ApiHealthSummary = z.infer<typeof apiHealthSummarySchema>

interface Sample {
  checkedAt: Date
  outcome: ApiHealthOutcome
  latencyMs: number
}

function percentile(sorted: number[], p: number): number | null {
  if (sorted.length === 0) return null
  return sorted[Math.min(sorted.length - 1, Math.ceil((p / 100) * sorted.length) - 1)] ?? null
}

const emptyCounts = () => ({ ok: 0, degraded: 0, down: 0, unreachable: 0, missing: 0 })

/** Samples into buckets of `bucketMinutes` over [from, to): counts per outcome, missing slots, latency. One pass. */
export function summarizeHealthSamples(
  samples: Sample[],
  range: { from: Date; to: Date; bucketMinutes: number },
): ApiHealthSummary {
  const bucketMs = range.bucketMinutes * 60_000
  const from = range.from.getTime()
  const to = range.to.getTime()
  const slotsPerBucket = Math.max(1, Math.round(range.bucketMinutes / API_HEALTH_SAMPLE_MINUTES))
  const count = Math.ceil((to - from) / bucketMs)
  const tallies = Array.from({ length: count }, () => ({
    ...emptyCounts(),
    latencies: [] as number[],
  }))
  for (const sample of samples) {
    const at = sample.checkedAt.getTime()
    if (at < from || at >= to) continue
    const tally = tallies[Math.floor((at - from) / bucketMs)]
    if (!tally) continue
    tally[sample.outcome] += 1
    tally.latencies.push(sample.latencyMs)
  }
  const totals = emptyCounts()
  const buckets = tallies.map((tally, index) => {
    const start = from + index * bucketMs
    const span = Math.min(bucketMs, to - start)
    const sampled = tally.ok + tally.degraded + tally.down + tally.unreachable
    const slots = Math.max(1, Math.round((slotsPerBucket * span) / bucketMs))
    const latencies = tally.latencies.sort((a, b) => a - b)
    const bucket = {
      ok: tally.ok,
      degraded: tally.degraded,
      down: tally.down,
      unreachable: tally.unreachable,
      missing: Math.max(0, slots - sampled),
    }
    for (const outcome of [...API_HEALTH_OUTCOMES, 'missing'] as const)
      totals[outcome] += bucket[outcome]
    return {
      start: new Date(start).toISOString(),
      ...bucket,
      p50LatencyMs: percentile(latencies, 50),
      p95LatencyMs: percentile(latencies, 95),
    }
  })
  const up = totals.ok + totals.degraded
  const measured = up + totals.down + totals.unreachable
  return {
    from: range.from.toISOString(),
    to: range.to.toISOString(),
    bucketMinutes: range.bucketMinutes,
    availabilityPercent: measured === 0 ? null : Math.floor((up / measured) * 100_000) / 1000,
    totals,
    buckets,
  }
}
